import { motion } from "framer-motion";
import { Coins, Shield, Zap, Users } from "lucide-react";

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_meme-coin-hub-10/artifacts/zcfvkldc_MultiVersus-Mojojojo.jpg";

const tokenStats = [
  { label: "Total Supply", value: "378T", icon: Coins },
  { label: "Tax", value: "0%", icon: Shield },
  { label: "Network", value: "ERC-20", icon: Zap },
  { label: "Holders", value: "Growing", icon: Users },
];

const Tokenomics = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="about" className="py-24 px-6 md:px-12 bg-[#05000A]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#9D4CDD] font-body text-xs tracking-[0.3em] uppercase mb-4">
            Evil Tokenomics
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            The Blueprint for <span className="text-[#FFD700]">World Domination</span>
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8"
        >
          {/* Main Token Info Card - Spans 8 columns */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-8 bento-card"
            data-testid="tokenomics-main-card"
          >
            <div className="space-y-6">
              <h3 className="font-heading text-2xl font-bold text-white">
                MojoCoin ($MOJO)
              </h3>
              <p className="text-[#A199AA] font-body leading-relaxed">
                Born from the genius mind of the world's most intelligent primate, 
                MojoCoin represents the pinnacle of meme coin evolution. Unlike those 
                pathetic Powerpuff Girls, $MOJO doesn't need Chemical X — it's powered 
                by pure evil genius and community strength.
              </p>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {tokenStats.map((stat, index) => (
                  <div 
                    key={index} 
                    className="p-4 bg-black/30 border border-[#9D4CDD]/20"
                    data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <stat.icon size={24} className="text-[#FFD700] mb-2" />
                    <p className="text-[#A199AA] text-xs uppercase tracking-wider mb-1">
                      {stat.label}
                    </p>
                    <p className="text-white font-heading text-xl font-bold">
                      {stat.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-3 mt-6">
                {["Renounced Contract", "LP Burned", "Community Driven", "No Presale"].map((feature, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-[#9D4CDD]/10 border border-[#9D4CDD]/30 text-[#9D4CDD] text-sm font-medium uppercase tracking-wider"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Image Card - Spans 4 columns */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-4 bento-card flex items-center justify-center p-4 relative overflow-hidden"
            data-testid="about-image-card"
          >
            {/* Neon border effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#9D4CDD]/20 to-[#FFD700]/20 opacity-50" />
            <img
              src={ABOUT_IMAGE}
              alt="Mojo Jojo Thinking"
              className="relative z-10 w-full max-w-[200px] object-contain drop-shadow-2xl"
              data-testid="about-image"
            />
          </motion.div>

          {/* Full Width Mission Card */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-12 bento-card"
            data-testid="mission-card"
          >
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 flex items-center justify-center">
                  <span className="text-[#FFD700] font-heading text-2xl font-bold">01</span>
                </div>
                <h4 className="font-heading text-xl font-bold text-white">Community First</h4>
                <p className="text-[#A199AA] text-sm leading-relaxed">
                  Unlike my previous failed plans, this one relies on the collective power of 
                  fellow evil geniuses. Together we rise!
                </p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 flex items-center justify-center">
                  <span className="text-[#FFD700] font-heading text-2xl font-bold">02</span>
                </div>
                <h4 className="font-heading text-xl font-bold text-white">Zero Taxes</h4>
                <p className="text-[#A199AA] text-sm leading-relaxed">
                  Mojo Jojo despises inefficiency. No buy tax. No sell tax. Every transaction 
                  is clean and pure like my magnificent brain.
                </p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 flex items-center justify-center">
                  <span className="text-[#FFD700] font-heading text-2xl font-bold">03</span>
                </div>
                <h4 className="font-heading text-xl font-bold text-white">Full Transparency</h4>
                <p className="text-[#A199AA] text-sm leading-relaxed">
                  Contract renounced, liquidity burned. Unlike the Mayor of Townsville, 
                  we have nothing to hide.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Tokenomics;
